import * as React from "react";
import { useCallback, useState } from "react";
import { Email } from "@src/types/api";
import { useTaskpaneServices } from "@src/load-agent/app/TaskpaneServicesContext";
import type { OutlookMailSession } from "@src/load-agent/types/office";
import type { LoadApplication } from "@src/load-agent/LoadApplication";

/** True when getEmails failed because the email row is not in Mongo yet (retry / processing UI). */
function isEmailNotFoundError(msg: string): boolean {
  if (!/email not found/i.test(msg)) return false;
  return /404/.test(msg) || /internetMessageId|globalId|conversationId/i.test(msg);
}

export async function fetchEmailForCurrentItem(
  office: OutlookMailSession,
  loadApp: Pick<LoadApplication, "getEmails">,
  signal: AbortSignal | undefined
): Promise<
  | { ok: true; data: Email }
  | { ok: false; reason: "not_found" }
  | { ok: false; reason: "other"; message: string }
> {
  await office.onReady();
  const meta = office.getItemMeta();

  if (!meta) {
    return { ok: false, reason: "other", message: "No email item in this Outlook window." };
  }
  const fromHeaders = await office.getThreadMessageIdCandidatesFromHeaders();
  const originalFromHeaders = await office.getOriginalInternetMessageIdFromHeaders();
  let primary = meta.internetMessageId?.trim() ?? "";

  if (!primary) {
    primary = (originalFromHeaders ?? "").trim();
  }
  if (!primary && fromHeaders.length > 0) {
    primary = fromHeaders[0].trim();
  }
  const conversationId = meta.conversationId?.trim() || null;
  const seen = new Set<string>();
  const extras: string[] = [];
  const pushExtra = (value: string | null | undefined) => {
    const trimmed = typeof value === "string" ? value.trim() : "";

    if (!trimmed || trimmed === primary || seen.has(trimmed)) return;
    seen.add(trimmed);
    extras.push(trimmed);
  };

  for (const id of fromHeaders) pushExtra(id);
  pushExtra(originalFromHeaders);

  if (!primary && !conversationId && extras.length === 0) {
    return {
      ok: false,
      reason: "other",
      message:
        "Cannot resolve thread for this draft: Outlook has not assigned an internet message id yet. Use a reply draft (In-Reply-To), open the original message in the thread, or save the draft so conversation metadata is available.",
    };
  }
  try {
    const data = await loadApp.getEmails(
      primary || undefined,
      signal,
      conversationId,
      extras.length > 0 ? extras : undefined,
      meta.ewsItemId
    );

    if (data) {
      return { ok: true, data };
    }
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : "Failed to fetch email data";

    if (!isEmailNotFoundError(msg)) {
      return { ok: false, reason: "other", message: msg };
    }
  }
  return { ok: false, reason: "not_found" };
}

function applyEmailFetchToUi(
  result: Awaited<ReturnType<typeof fetchEmailForCurrentItem>>,
  setEmailData: (v: Email | null) => void,
  setError: (v: string | null) => void,
  setIsProcessing: (v: boolean) => void,
  opts?: { clearErrorOnSuccess?: boolean }
): void {
  if (result.ok) {
    setEmailData(result.data);
    setIsProcessing(false);
    if (opts?.clearErrorOnSuccess) setError(null);
  } else if (result.reason === "not_found") {
    setIsProcessing(true);
    setError(null);
  } else {
    setError(result.message);
    setIsProcessing(false);
  }
}

async function runInitialEmailLoad(
  office: OutlookMailSession,
  loadApp: Pick<LoadApplication, "getEmails">,
  signal: AbortSignal,
  isMounted: () => boolean,
  setEmailData: (v: Email | null) => void,
  setError: (v: string | null) => void,
  setLoading: (v: boolean) => void,
  setIsProcessing: (v: boolean) => void
): Promise<void> {
  try {
    setLoading(true);
    setError(null);
    setIsProcessing(false);
    await office.onReady();
    if (!isMounted()) return;
    const meta = office.getItemMeta();

    if (!meta) {
      setError("No email selected / mailbox not ready.");
      setLoading(false);
      return;
    }
    const result = await fetchEmailForCurrentItem(office, loadApp, signal);

    if (!isMounted()) return;
    applyEmailFetchToUi(result, setEmailData, setError, setIsProcessing);
  } catch (e: unknown) {
    if (signal.aborted) return;
    if (isMounted()) {
      const msg = e instanceof Error ? e.message : "Failed to fetch email data";

      setError(msg);
      setIsProcessing(false);
    }
  } finally {
    if (isMounted()) setLoading(false);
  }
}

function getActorEmail(): string | undefined {
  return Office.context?.mailbox?.userProfile?.emailAddress?.trim() || undefined;
}

function useEmailInteractionLeadRecording(
  internetMessageId: string | undefined,
  loadApp: Pick<LoadApplication, "recordEmailInteractionLead">
): void {
  const recorded = React.useRef(false);

  React.useEffect(() => {
    if (!internetMessageId || recorded.current) return;
    try {
      const actorEmail = getActorEmail();

      if (!actorEmail) return;
      recorded.current = true;
      void loadApp.recordEmailInteractionLead({
        internetMessageId,
        actorEmail,
      });
    } catch {
      // Non-blocking.
    }
  }, [internetMessageId, loadApp]);
}

function useInitialEmailDataLoad(
  office: OutlookMailSession,
  loadApp: Pick<LoadApplication, "getEmails">,
  retryCount: number,
  setEmailData: (v: Email | null) => void,
  setError: (v: string | null) => void,
  setLoading: (v: boolean) => void,
  setIsProcessing: (v: boolean) => void
): void {
  React.useEffect(() => {
    const controller = new AbortController();
    let mounted = true;

    void runInitialEmailLoad(
      office,
      loadApp,
      controller.signal,
      () => mounted,
      setEmailData,
      setError,
      setLoading,
      setIsProcessing
    );
    return () => {
      mounted = false;
      controller.abort();
    };
  }, [office, loadApp, retryCount]);
}

export function useEmailData() {
  const { office, loadApp } = useTaskpaneServices();
  const [emailData, setEmailData] = useState<Email | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEmailInteractionLeadRecording(emailData?.internetMessageId, loadApp);
  useInitialEmailDataLoad(
    office,
    loadApp,
    retryCount,
    setEmailData,
    setError,
    setLoading,
    setIsProcessing
  );
  const refreshEmailData = useCallback(async (): Promise<Email | null> => {
    const showLoading = emailData == null;

    try {
      if (showLoading) setLoading(true);
      const result = await fetchEmailForCurrentItem(office, loadApp, undefined);

      applyEmailFetchToUi(result, setEmailData, setError, setIsProcessing, {
        clearErrorOnSuccess: true,
      });
      return result.ok ? result.data : null;
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : "Failed to refresh email data");
      setIsProcessing(false);
      return null;
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [office, loadApp, emailData]);
  const retryFetch = useCallback(() => {
    setRetryCount((count) => count + 1);
  }, []);
  const resetAndReloadForCurrentItem = useCallback(() => {
    setEmailData(null);
    setError(null);
    setIsProcessing(false);
    setLoading(true);
    retryFetch();
  }, [retryFetch]);

  return {
    emailData,
    setEmailData,
    refreshEmailData,
    retryFetch,
    resetAndReloadForCurrentItem,
    loading,
    error,
    isProcessing,
  };
}
